import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Grid, IconButton, TextField, Typography, MenuItem, Switch,
  Divider, CircularProgress, alpha, Chip, Tooltip, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
} from "@mui/material";
import {
  DragDropContext, Droppable, Draggable, DropResult,
} from "@hello-pangea/dnd";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SearchIcon from "@mui/icons-material/Search";
import TimerIcon from "@mui/icons-material/Timer";
import {
  useGetPlaylistGroupQuery,
  useUpdatePlaylistGroupMutation,
  useGetPlaylistsQuery,
  useAddPlaylistToGroupMutation,
  useUpdatePlaylistGroupItemMutation,
  useRemovePlaylistFromGroupMutation,
  useReorderPlaylistGroupItemsMutation,
} from "../../../Api/attoDeskApi";
import {
  IPlaylist, IPlaylistGroup, IPlaylistGroupItem,
} from "../../../Api/Interface/api.interface";
import PageLoader from "../../../Components/Loader/PageLoader";
import { useNotifier } from "../../../Core/Notifier";
import {
  NAVY, NAVY_DARK, GOLD, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";
import SignageSection, {
  signageLabelSx, signageInputSx,
} from "../../../Components/ui/SignageSection";

const PLAYBACK_MODES = [
  { v: "SEQUENTIAL", label: "Sequential", help: "Play each playlist in order, then start over" },
  { v: "LOOP_ALL", label: "Loop All", help: "Continuously loop through every playlist" },
  { v: "RANDOM", label: "Random", help: "Shuffle the order on every cycle" },
];

const formatMinutes = (m: number): string => {
  if (!m || m <= 0) return "—";
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h > 0) return `${h}h ${r}m`;
  return `${r}m`;
};

const PlaylistGroupEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const groupId = Number(id);
  const { showMessage, showErrorMessage } = useNotifier();

  const { data, isLoading, refetch } = useGetPlaylistGroupQuery(groupId);
  const [updateGroup, { isLoading: isSaving }] = useUpdatePlaylistGroupMutation();
  const [addPlaylist] = useAddPlaylistToGroupMutation();
  const [updateItem] = useUpdatePlaylistGroupItemMutation();
  const [removeItem] = useRemovePlaylistFromGroupMutation();
  const [reorder, { isLoading: isReordering }] = useReorderPlaylistGroupItemsMutation();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [playbackMode, setPlaybackMode] = useState("SEQUENTIAL");
  const [isActive, setIsActive] = useState(true);
  const [items, setItems] = useState<IPlaylistGroupItem[]>([]);

  const [pickerOpen, setPickerOpen] = useState(false);

  const group = data?.data as unknown as IPlaylistGroup | undefined;

  useEffect(() => {
    if (group) {
      setName(group.name);
      setDescription(group.description ?? "");
      setPlaybackMode(group.playback_mode);
      setIsActive(group.is_active);
      setItems(group.items ?? []);
    }
  }, [group]);

  const stats = useMemo(() => {
    const active = items.filter(i => i.is_active).length;
    const inactive = items.length - active;
    return { active, inactive, total: items.length };
  }, [items]);

  const handleSaveMeta = async () => {
    try {
      const res = await updateGroup({
        id: groupId,
        body: {
          name: name.trim(),
          description: description.trim() || null,
          playback_mode: playbackMode,
          is_active: isActive,
        },
      }).unwrap();
      if (res.status) { showMessage("Saved"); refetch(); }
    } catch { showErrorMessage("Failed to save"); }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    const next = [...items];
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setItems(next);
    try {
      const res = await reorder({
        id: groupId,
        item_ids: next.map(i => i.id),
      }).unwrap();
      if (res.status) { showMessage("Order saved"); refetch(); }
    } catch {
      showErrorMessage("Failed to save order");
      refetch();
    }
  };

  const handleAdd = async (playlist: IPlaylist) => {
    try {
      const res = await addPlaylist({
        id: groupId,
        playlist_id: playlist.id,
      }).unwrap();
      if (res.status) {
        showMessage(`Added "${playlist.name}"`);
        refetch();
      }
    } catch { showErrorMessage("Failed to add"); }
  };

  const handleToggleActive = async (item: IPlaylistGroupItem) => {
    try {
      const res = await updateItem({
        groupId,
        itemId: item.id,
        body: { is_active: !item.is_active },
      }).unwrap();
      if (res.status) { refetch(); }
    } catch { showErrorMessage("Failed to update"); }
  };

  const handleSetDuration = async (item: IPlaylistGroupItem, minutes: number | null) => {
    try {
      const res = await updateItem({
        groupId,
        itemId: item.id,
        body: { duration_minutes: minutes },
      }).unwrap();
      if (res.status) { refetch(); }
    } catch { showErrorMessage("Failed to update duration"); }
  };

  const handleRemove = async (itemId: number) => {
    try {
      const res = await removeItem({ groupId, itemId }).unwrap();
      if (res.status) { showMessage("Removed"); refetch(); }
    } catch { showErrorMessage("Failed to remove"); }
  };

  if (isLoading || !group) return <PageLoader height="20vh" />;

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      {/* Header */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, position: "sticky", top: 0, zIndex: 10,
        boxShadow: "0 4px 30px rgba(0,0,0,0.18)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
          <IconButton onClick={() => navigate("/playlist-groups/list")} size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2,
              width: 38, height: 38, bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
            }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
            boxShadow: "0 4px 12px rgba(124,58,237,0.45)",
          }}>
            <CollectionsBookmarkIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Typography fontSize={20} fontWeight={800} color="#fff" letterSpacing="-0.02em"
                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 320 }}>
                {name || "Untitled Group"}
              </Typography>
              <Chip size="small" label={group.playback_mode}
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700,
                  bgcolor: "rgba(124,58,237,0.18)", color: "#c4b5fd",
                  border: "1px solid rgba(124,58,237,0.35)",
                }} />
              <Chip size="small"
                label={isActive ? "Active" : "Inactive"}
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700,
                  bgcolor: isActive ? "rgba(16,185,129,0.18)" : "rgba(148,163,184,0.18)",
                  color: isActive ? "#a7f3d0" : "#cbd5e1",
                  border: `1px solid ${isActive ? "rgba(16,185,129,0.35)" : "rgba(148,163,184,0.3)"}`,
                }} />
            </Box>
            <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
              <Typography fontSize={11.5} color="rgba(255,255,255,0.6)">
                {stats.total} playlist{stats.total === 1 ? "" : "s"} · {stats.active} active
              </Typography>
              <Typography fontSize={11.5} color="rgba(255,255,255,0.6)">
                {group.devices_count ?? 0} device{(group.devices_count ?? 0) === 1 ? "" : "s"} assigned
              </Typography>
              {group.total_duration > 0 && (
                <Typography fontSize={11.5} color="rgba(255,255,255,0.6)">
                  Estimated runtime ~ {formatMinutes(group.total_duration)}
                </Typography>
              )}
            </Box>
          </Box>
          <Button variant="contained" disableElevation size="small"
            disabled={isSaving} onClick={handleSaveMeta}
            startIcon={isSaving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
              background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
              "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
            }}>
            {isSaving ? "Saving…" : "Save Details"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* LEFT: meta */}
          <Grid item xs={12} lg={4}>
            <SignageSection
              icon={<InfoOutlinedIcon sx={{ fontSize: 18, color: "#3b82f6" }} />}
              title="Group Information"
              accentColor="#3b82f6"
            >
              <Typography sx={signageLabelSx}>Group Name *</Typography>
              <TextField fullWidth value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5, fontSize: 15, fontWeight: 600, bgcolor: "#fff",
                    "& fieldset": { borderColor: "#e2e8f0" },
                    "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: 2 },
                  },
                }} />
              <Typography sx={signageLabelSx}>Description</Typography>
              <TextField fullWidth size="small" multiline rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{ mb: 2, ...signageInputSx }} />
            </SignageSection>

            <SignageSection
              icon={<SettingsIcon sx={{ fontSize: 18, color: "#7c3aed" }} />}
              title="Playback Settings"
              accentColor="#7c3aed"
            >
              <Typography sx={signageLabelSx}>Playback Mode</Typography>
              <TextField select fullWidth size="small"
                value={playbackMode}
                onChange={(e) => setPlaybackMode(e.target.value)}
                sx={{ mb: 2, ...signageInputSx }}>
                {PLAYBACK_MODES.map(m => (
                  <MenuItem key={m.v} value={m.v}>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{m.label}</Typography>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{m.help}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                p: 2, borderRadius: 2.5,
                bgcolor: isActive ? alpha("#10b981", 0.05) : "#f8fafc",
                border: `1px solid ${isActive ? alpha("#10b981", 0.2) : "#e2e8f0"}`,
              }}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>Active</Typography>
                  <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                    Devices only receive active groups
                  </Typography>
                </Box>
                <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              </Box>
            </SignageSection>
          </Grid>

          {/* RIGHT: ordered playlists */}
          <Grid item xs={12} lg={8}>
            <SignageSection
              icon={<PlaylistPlayIcon sx={{ fontSize: 18, color: "#7c3aed" }} />}
              title={`Assigned Playlists (${stats.total})`}
              accentColor="#7c3aed"
              subtitle="Drag to reorder · click the eye to enable/disable · click the timer to set per-group duration"
              right={
                <Button variant="contained" disableElevation size="small"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setPickerOpen(true)}
                  sx={{
                    borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700,
                    background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
                    "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
                  }}>
                  Add Playlist
                </Button>
              }
            >
              {items.length === 0 ? (
                <Box sx={{
                  py: 5, textAlign: "center",
                  border: `2px dashed ${BORDER}`, borderRadius: 3, bgcolor: "#f8fafc",
                }}>
                  <PlaylistPlayIcon sx={{ fontSize: 36, color: "#cbd5e1", mb: 1 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY, mb: 0.5 }}>
                    No playlists in this group yet
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mb: 2 }}>
                    Add playlists you've already built — the group plays them in order.
                  </Typography>
                  <Button variant="contained" disableElevation
                    startIcon={<AddIcon />}
                    onClick={() => setPickerOpen(true)}
                    sx={{
                      borderRadius: 2.5, textTransform: "none", fontWeight: 700,
                      background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
                    }}>
                    Add Playlist
                  </Button>
                </Box>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="group-items">
                    {(provided) => (
                      <Box ref={provided.innerRef} {...provided.droppableProps}>
                        {items.map((item, index) => (
                          <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                            {(p, snap) => {
                              const pl = item.playlist;
                              const dim = !item.is_active;
                              return (
                                <Box ref={p.innerRef} {...p.draggableProps}
                                  sx={{
                                    display: "flex", alignItems: "center", gap: 2, p: 1.5, mb: 1.25,
                                    borderRadius: 2.5,
                                    bgcolor: snap.isDragging
                                      ? alpha("#7c3aed", 0.08)
                                      : (dim ? "#f8fafc" : "#fff"),
                                    border: `1px solid ${snap.isDragging ? alpha("#7c3aed", 0.4) : BORDER}`,
                                    boxShadow: snap.isDragging
                                      ? `0 12px 28px ${alpha("#7c3aed", 0.22)}`
                                      : "0 1px 2px rgba(11,30,45,0.04)",
                                    opacity: dim ? 0.65 : 1,
                                    transition: "all 0.15s ease",
                                  }}>
                                  <Box {...p.dragHandleProps}
                                    sx={{
                                      cursor: "grab", color: "#cbd5e1",
                                      "&:active": { cursor: "grabbing" },
                                      "&:hover": { color: "#7c3aed" },
                                    }}>
                                    <DragIndicatorIcon />
                                  </Box>
                                  <Box sx={{
                                    width: 36, height: 36, borderRadius: "10px",
                                    background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.15)} 0%, ${alpha("#7c3aed", 0.05)} 100%)`,
                                    border: `1px solid ${alpha("#7c3aed", 0.2)}`,
                                    color: "#7c3aed",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 13, fontWeight: 800,
                                  }}>
                                    {String(index + 1).padStart(2, "0")}
                                  </Box>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{
                                      fontSize: 14, fontWeight: 700, color: NAVY,
                                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                    }}>
                                      {pl?.name || "—"}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", mt: 0.75, flexWrap: "wrap" }}>
                                      <Chip size="small" icon={<PermMediaIcon sx={{ fontSize: 12 }} />}
                                        label={`${pl?.items_count ?? 0} item${(pl?.items_count ?? 0) === 1 ? "" : "s"}`}
                                        sx={{
                                          height: 20, fontSize: 11, fontWeight: 700,
                                          bgcolor: "#dbeafe", color: "#1e40af",
                                          border: "1px solid #bfdbfe",
                                          "& .MuiChip-icon": { color: "#3b82f6", ml: 0.5 },
                                        }} />
                                      <Chip size="small"
                                        label={pl?.duration_mode || "PLAY_FULL"}
                                        sx={{
                                          height: 20, fontSize: 10, fontWeight: 700,
                                          bgcolor: "#eef2ff", color: "#4338ca",
                                          border: "1px solid #c7d2fe",
                                        }} />
                                      <Chip size="small" icon={<TimerIcon sx={{ fontSize: 11 }} />}
                                        label={item.duration_minutes
                                          ? `${item.duration_minutes} min`
                                          : (pl?.duration_minutes ? `${pl.duration_minutes} min` : "play full")}
                                        onClick={() => {
                                          const cur = item.duration_minutes ?? pl?.duration_minutes ?? null;
                                          const next = window.prompt(
                                            "Override duration (minutes). Leave blank to use playlist default.",
                                            cur ? String(cur) : ""
                                          );
                                          if (next === null) return;
                                          const n = next.trim() === "" ? null : Math.max(1, parseInt(next, 10) || 0);
                                          handleSetDuration(item, n);
                                        }}
                                        sx={{
                                          height: 20, fontSize: 11, fontWeight: 700, cursor: "pointer",
                                          bgcolor: "#fef3c7", color: "#92400e",
                                          border: "1px solid #fcd34d",
                                          "& .MuiChip-icon": { color: "#d97706", ml: 0.5 },
                                          "&:hover": { bgcolor: "#fde68a" },
                                        }} />
                                      {pl && !pl.is_active && (
                                        <Chip size="small" label="Playlist Inactive"
                                          sx={{
                                            height: 20, fontSize: 10, fontWeight: 700,
                                            bgcolor: "#fee2e2", color: "#b91c1c",
                                            border: "1px solid #fecaca",
                                          }} />
                                      )}
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: "flex", gap: 0.5 }}>
                                    <Tooltip title={item.is_active ? "Disable in playback" : "Enable in playback"}>
                                      <IconButton size="small" onClick={() => handleToggleActive(item)}
                                        sx={{
                                          color: item.is_active ? "#10b981" : "#94a3b8",
                                          "&:hover": { bgcolor: item.is_active ? "#ecfdf5" : "#f1f5f9" },
                                        }}>
                                        {item.is_active ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Open playlist">
                                      <IconButton size="small"
                                        onClick={() => pl && navigate(`/playlist/edit/${pl.id}`)}
                                        sx={{ color: "#3b82f6", "&:hover": { bgcolor: alpha("#3b82f6", 0.08) } }}>
                                        <PlaylistPlayIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remove from group">
                                      <IconButton size="small" onClick={() => handleRemove(item.id)}
                                        sx={{ color: "#94a3b8", "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" } }}>
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              );
                            }}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
              {isReordering && (
                <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 1, textAlign: "right" }}>
                  Saving order…
                </Typography>
              )}
            </SignageSection>
          </Grid>
        </Grid>
      </Box>

      <AddPlaylistDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(p) => { handleAdd(p); setPickerOpen(false); }}
        excludeIds={new Set(items.map(i => i.playlist_id))}
      />
    </Box>
  );
};

/* ───────────────────────────────────────────────────────────────────────── */

const AddPlaylistDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onPick: (p: IPlaylist) => void;
  excludeIds: Set<number>;
}> = ({ open, onClose, onPick, excludeIds }) => {
  const [search, setSearch] = useState("");
  const { data } = useGetPlaylistsQuery({ searchText: search });

  const playlists: IPlaylist[] = useMemo(() =>
    (Array.isArray(data?.data) ? ((data?.data as unknown) as IPlaylist[]) : [])
      .filter(p => p.is_active && !excludeIds.has(p.id)),
  [data, excludeIds]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: NAVY }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 2,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.12)} 0%, ${alpha("#7c3aed", 0.06)} 100%)`,
          }}>
            <PlaylistPlayIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
          </Box>
          Add Playlist to Group
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth size="small"
          placeholder="Search playlists"
          value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ mt: 1, mb: 2, ...signageInputSx }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }} />
        {playlists.length === 0 ? (
          <Typography sx={{ color: TEXT_SECONDARY, textAlign: "center", py: 4, fontSize: 13 }}>
            No active playlists available to add.
          </Typography>
        ) : (
          <Box>
            {playlists.map(p => (
              <Paper key={p.id} elevation={0}
                onClick={() => onPick(p)}
                sx={{
                  p: 1.75, mb: 1, borderRadius: 2.5,
                  border: `1px solid ${BORDER}`,
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: "#7c3aed",
                    transform: "translateY(-1px)",
                    boxShadow: `0 6px 16px ${alpha("#7c3aed", 0.15)}`,
                  },
                  display: "flex", alignItems: "center", gap: 2,
                }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 1.5,
                  bgcolor: alpha("#7c3aed", 0.12), color: "#7c3aed",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <PlaylistPlayIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                    {p.name}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                    <Chip size="small" label={`v${p.version}`} sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: alpha(GOLD, 0.12), color: "#b58a13" }} />
                    <Chip size="small" label={p.duration_mode || "PLAY_FULL"} sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#eef2ff", color: "#4338ca" }} />
                    <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
                      {p.items_count ?? 0} items
                    </Typography>
                  </Box>
                </Box>
                <AddIcon sx={{ color: "#7c3aed" }} />
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}
          sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#64748b" }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlaylistGroupEdit;
